import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './EditLearningPathPage.module.scss'
import LearningPathForm, { LearningPathFormValues } from '../../components/LearningPathForm/LearningPathForm'
import useRoutes from '../../state/useRoutes'
import { FormikErrors } from 'formik/dist/types'
import setFormErrors from '../../utilities/setFormErrors'
import useUpdateLearningPath, { UpdateLearningPathArgs } from '../../state/mutations/useUpdateLearningPath'
import { useParams } from 'react-router-dom'
import { AnonymousLearningItem } from '../../models/AnonymousLearningItem'
import { areCollectionsDifferentByUuid } from '../../utilities/collections'
import { isLearningItemAsPath, LearningItemAsPath } from '../../models/LearningItem'
import usePathLearningItems from '../../state/queries/usePathLearningItems'

export const EditLearningPathPage = React.memo(function EditLearningPathPage2({
  learningItem,
}: {
  learningItem: AnonymousLearningItem
}) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { uuid } = useParams()
  const handleClose = useCallback(() => routes.course.goto({ uuid: uuid! }), [routes])
  const updateLearningPath = useUpdateLearningPath()

  if (learningItem.error) {
    handleClose()
  }

  async function submitEditing(
    values: LearningPathFormValues,
    setStatus: (status?: any) => void,
    setErrors: (errors: FormikErrors<LearningPathFormValues>) => void
  ) {
    setStatus()

    let learningItemAsPath: LearningItemAsPath | null = null
    if (isLearningItemAsPath(learningItem)) {
      learningItemAsPath = learningItem
    }

    try {
      const dataToUpdate = prepareDataForUpdate(values, learningItemAsPath!)
      if (isUpdateDataEmpty(dataToUpdate)) {
        setStatus(t('learning item.nothing has changed in the form'))

        return
      }

      const updatedLearningItemAsPath = await updateLearningPath.mutateAsync(dataToUpdate).catch((error) => {
        setFormErrors({ setStatus, setErrors, t }, error)
      })

      routes.course.goto({ uuid: updatedLearningItemAsPath!.uuid })
    } catch (error) {
      setFormErrors({ setStatus, setErrors, t }, error)
    }
  }

  return (
    <article className={styles.EditLearningPathPage}>
      <LearningPathForm
        initialValues={useInitialValues(learningItem)}
        onClose={handleClose}
        onSubmit={(data, setStatus, setErrors) => submitEditing(data, setStatus, setErrors)}
      />
    </article>
  )
})

function useInitialValues(learningItem: AnonymousLearningItem): LearningPathFormValues {
  const { pathLearningItems } = usePathLearningItems(learningItem.uuid)

  return {
    title: learningItem.title,
    description: learningItem.description,
    status: learningItem.status,
    image: learningItem.imageFileName,
    certificateMode: learningItem.createCertificate
      ? learningItem.certificateValidUntil
        ? 'limited'
        : 'unlimited'
      : 'none',
    certificateValidity: learningItem.certificateValidUntil,
    categories: learningItem.categories,
    courses: pathLearningItems,
    isAnyoneAssigned: learningItem.userLearningAssignments ? learningItem.userLearningAssignments.length > 0 : false,
  }
}

function isUpdateDataEmpty(dataToUpdate: any): boolean {
  return Object.entries(dataToUpdate)
    .filter(([key]) => key !== 'uuid')
    .every(([, value]) => value === undefined)
}

function prepareDataForUpdate(
  values: LearningPathFormValues,
  learningItem: LearningItemAsPath
): UpdateLearningPathArgs {
  const createCertificate = values.certificateMode !== 'none'

  const dataToUpdate = {
    uuid: learningItem.uuid,
    status: values.status !== learningItem.status ? values.status : undefined,
    title: values.title !== learningItem.title ? values.title : undefined,

    // When the image is not changed, we will see: http://api.skillfit... path to the image API
    // When the image is changed, we will see: data:image/png;base64,.... base64 image
    image: values.image?.startsWith('data:') ? values.image?.replace(/^[^,]+,/, '') : undefined,

    description: values.description.trim() !== learningItem.description.trim() ? values.description : undefined,

    categories: areCollectionsDifferentByUuid(values?.categories ?? [], learningItem.categories)
      ? values.categories!.map(({ uuid }) => uuid)
      : undefined,

    createCertificate: createCertificate !== learningItem.createCertificate ? createCertificate : undefined,
    certificateValidUntil:
      values.certificateValidity !== learningItem.certificateValidUntil
        ? values.certificateMode === 'limited'
          ? values.certificateValidity
          : null
        : undefined,

    courses: areCollectionsDifferentByUuid(values?.courses ?? [], learningItem.learningPath.courses || [])
      ? values.courses!.map(({ uuid }) => uuid)
      : undefined,
  }

  if (false === dataToUpdate.createCertificate) {
    dataToUpdate.certificateValidUntil = learningItem.certificateValidUntil ? null : undefined
  }

  return dataToUpdate
}
